// src/pages/journal/JournalEntryPage.tsx - Journal entry creation/editing page

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { 
  fetchJournalEntry, 
  createJournalEntry, 
  updateJournalEntry 
} from '../../store/slices/journalSlice';
import { AppDispatch, RootState } from '../../store';
import { useAuth } from '../../contexts/AuthContext';

const JournalEntryPage: React.FC = () => {
  const { entryId } = useParams<{ entryId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  
  const { currentEntry, isLoading, error } = useSelector((state: RootState) => state.journal);
  
  const [content, setContent] = useState('');
  const [mood, setMood] = useState('');
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [reflectionDialogOpen, setReflectionDialogOpen] = useState(false);
  
  useEffect(() => {
    if (entryId && currentUser) {
      dispatch(fetchJournalEntry(entryId));
    }
  }, [entryId, currentUser, dispatch]);
  
  useEffect(() => {
    if (currentEntry) {
      setContent(currentEntry.content);
      setMood(currentEntry.mood || '');
    }
  }, [currentEntry]);
  
  const handleSave = async () => {
    if (!currentUser || !content.trim()) return;
    
    const entryData = {
      userId: currentUser.uid,
      content: content.trim(),
      mood: mood || undefined,
      timestamp: Date.now(),
    };
    
    let result;
    
    if (entryId) {
      // Update existing entry
      result = await dispatch(updateJournalEntry({
        entryId,
        updates: entryData
      }));
      
      if (updateJournalEntry.fulfilled.match(result)) {
        navigate('/journal');
      }
    } else {
      // Create new entry
      result = await dispatch(createJournalEntry(entryData));
      
      if (createJournalEntry.fulfilled.match(result)) {
        setReflectionDialogOpen(true);
      }
    }
  };
  
  const handleGenerateReflection = () => {
    setReflectionDialogOpen(false);
    
    if (currentEntry?.id) {
      navigate(`/journal/reflection/${currentEntry.id}`);
    }
  };
  
  const handleSkipReflection = () => {
    setReflectionDialogOpen(false);
    navigate('/journal');
  };
  
  const handleCancel = () => {
    if (content.trim() !== (currentEntry?.content || '')) {
      setDiscardDialogOpen(true);
    } else {
      navigate('/journal');
    }
  };
  
  const handleDiscard = () => {
    setDiscardDialogOpen(false);
    navigate('/journal');
  };
  
  const handleKeepEditing = () => {
    setDiscardDialogOpen(false);
  };
  
  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography component="h1" variant="h4" sx={{ mb: 4 }}>
          {entryId ? 'Edit Journal Entry' : 'New Journal Entry'}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="mood-label">How are you feeling?</InputLabel>
            <Select
              labelId="mood-label"
              id="mood"
              value={mood}
              label="How are you feeling?"
              onChange={(e) => setMood(e.target.value)}
              disabled={isLoading}
            >
              <MenuItem value="">
                <em>Choose a mood</em>
              </MenuItem>
              <MenuItem value="Great">Great</MenuItem>
              <MenuItem value="Good">Good</MenuItem>
              <MenuItem value="Neutral">Neutral</MenuItem>
              <MenuItem value="Bad">Bad</MenuItem>
              <MenuItem value="Terrible">Terrible</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            id="content"
            label="Journal Entry"
            multiline
            rows={12}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind today? Write freely about your thoughts, challenges, victories, or insights..."
            disabled={isLoading}
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isLoading || !content.trim()}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </Box>
        </Paper>
      </Box>
      
      {/* Discard Changes Dialog */}
      <Dialog
        open={discardDialogOpen}
        onClose={handleKeepEditing}
      >
        <DialogTitle>Discard Changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to discard them?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepEditing}>Keep Editing</Button>
          <Button onClick={handleDiscard} color="error">Discard</Button>
        </DialogActions>
      </Dialog>
      
      {/* Reflection Dialog */}
      <Dialog
        open={reflectionDialogOpen}
        onClose={handleSkipReflection}
      >
        <DialogTitle>Generate Reflection?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Would you like to generate an AI-powered reflection on your journal entry? This can help provide deeper insights and patterns.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSkipReflection}>Skip</Button>
          <Button onClick={handleGenerateReflection} variant="contained">Generate Reflection</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JournalEntryPage;
